import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { diversificationMatrixService } from '../services/diversificationMatrixApi';
import { useTheme } from '../context/ThemeContext';

const DiversificationOpportunityMatrix = ({ selectedMineral = 'Lithium' }) => {
  const { isDarkMode } = useTheme();
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);

  // Dynamic Nivo theme function
  const getNivoTheme = (isDark) => {
    return {
      background: 'transparent',
      text: {
        fontSize: 11,
        fill: isDark ? '#d1d5db' : '#374151'
      },
      tooltip: {
        container: {
          background: isDark ? '#1f2937' : '#ffffff',
          color: isDark ? '#ffffff' : '#1f2937',
          fontSize: '12px',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
          border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db'
        }
      },
      axis: {
        domain: {
          line: {
            stroke: isDark ? '#6b7280' : '#d1d5db',
            strokeWidth: 1
          }
        },
        legend: {
          text: {
            fontSize: 12,
            fill: isDark ? '#f3f4f6' : '#374151',
            fontWeight: 500
          }
        },
        ticks: {
          line: {
            stroke: isDark ? '#6b7280' : '#d1d5db',
            strokeWidth: 1
          },
          text: {
            fontSize: 11,
            fill: isDark ? '#d1d5db' : '#374151'
          }
        }
      },
      grid: {
        line: {
          stroke: isDark ? '#4b5563' : '#e5e7eb',
          strokeWidth: 1
        }
      }
    };
  };

  // Load matrix data when mineral changes
  useEffect(() => {
    const loadMatrixData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await diversificationMatrixService.getMatrixData(selectedMineral);
        setMatrixData(data);
      } catch (err) {
        console.error('Error loading diversification matrix data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadMatrixData();
  }, [selectedMineral]);

  // Transform data for Nivo ScatterPlot
  const transformDataForNivo = () => {
    if (!matrixData?.matrixData) {
      console.log('No matrix data available');
      return [];
    }

    console.log('Transforming data for', matrixData.matrixData.length, 'countries');

    // Create a single series with all countries (simpler approach)
    const allCountries = matrixData.matrixData
      .filter(country => 
        country.eci !== undefined && 
        country.pri !== undefined &&
        country.eci !== null &&
        country.pri !== null
      )
      .map(country => {
        console.log(`Country: ${country.country}, ECI: ${country.eci}, PRI: ${country.pri}, ISO3: ${country.iso3}`);
        
        return {
          x: parseFloat(country.eci) || 0,
          y: parseFloat(country.pri) || 3.0,
          country: country.country,
          iso3: country.iso3 || 'N/A',
          reservesPercentage: country.reservesPercentage || 0,
          importSharePercentage: country.importSharePercentage || 0,
          untappedOpportunity: country.untappedOpportunity || 0,
          opportunityLevel: country.opportunityLevel || 'Unknown',
          geopoliticalLabel: country.geopoliticalLabel || 'Unknown'
        };
      });

    console.log('Transformed data points:', allCountries.length);
    console.log('Sample point:', allCountries[0]);

    // Group by opportunity level for proper coloring
    const opportunityGroups = {
      'High Opportunity': { data: [], color: '#10b981' },
      'Medium Opportunity': { data: [], color: '#3b82f6' },
      'Low Opportunity': { data: [], color: '#f59e0b' },
      'Over-exposed': { data: [], color: '#ef4444' }
    };

    allCountries.forEach(country => {
      const opportunity = country.untappedOpportunity || 0;
      let group;
      if (opportunity >= 20) group = 'High Opportunity';
      else if (opportunity >= 10) group = 'Medium Opportunity';
      else if (opportunity >= 0) group = 'Low Opportunity';
      else group = 'Over-exposed';

      opportunityGroups[group].data.push(country);
    });

    return Object.keys(opportunityGroups)
      .filter(key => opportunityGroups[key].data.length > 0)
      .map(key => ({
        id: key,
        data: opportunityGroups[key].data
      }));
  };

  // Get color for data points based on untapped opportunity
  const getNodeColor = (node) => {
    if (!node || !node.data) return '#6b7280'; // Default gray
    const opportunity = node.data.untappedOpportunity || 0;
    if (opportunity >= 20) return '#10b981'; // High opportunity - green
    if (opportunity >= 10) return '#3b82f6'; // Medium opportunity - blue
    if (opportunity >= 0) return '#f59e0b';  // Low opportunity - yellow
    return '#ef4444'; // Over-exposed - red
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 transition-colors duration-200">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading Diversification Matrix...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 transition-colors duration-200">
        <div className="text-center py-12">
          <div className="text-red-600 dark:text-red-400 text-lg mb-2">⚠️ Error Loading Data</div>
          <p className="text-gray-600 dark:text-gray-300">{error}</p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
            Using mock data for demonstration purposes
          </p>
        </div>
      </div>
    );
  }

  // No data state
  if (!matrixData?.matrixData || matrixData.matrixData.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 transition-colors duration-200">
        <div className="text-center py-12">
          <div className="text-yellow-600 dark:text-yellow-400 text-lg mb-2">⚠️ No Data Available</div>
          <p className="text-gray-600 dark:text-gray-300">No diversification matrix data available for {selectedMineral}</p>
        </div>
      </div>
    );
  }

  const nivoData = transformDataForNivo();

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 transition-colors duration-200" style={{ overflow: 'visible' }}>
      <div className="mb-2">
        <div className="flex items-center">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          {selectedMineral} - Diversification Opportunity Matrix
          </h3>
        </div>
     
        
        {/* Legend
        <div className="flex flex-wrap gap-4 text-xs text-gray-300 mb-4">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
            <span>High Opportunity (≥20%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded mr-1"></div>
            <span>Medium Opportunity (10-20%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded mr-1"></div>
            <span>Low Opportunity (0-10%)</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
            <span>Over-exposed (0-10%)</span>
          </div>
        </div> */}
      </div>

      {/* Chart Container */}
      <div style={{ height: '500px', position: 'relative', overflow: 'visible' }}>
        <ResponsiveScatterPlot
          data={nivoData}
          margin={{ top: 40, right: 120, bottom: 80, left: 70 }}
          xScale={{ type: 'linear', min: -2.5, max: 2.5 }}
          yScale={{ type: 'linear', min: 1, max: 5 }}
          nodeSize={(node) => Math.max(15, Math.sqrt(node.data.reservesPercentage || 0) * 10)}
          colors={['#10b981', '#3b82f6', '#f59e0b', '#ef4444']}
          enableGridX={true}
          enableGridY={true}
          pointSize={10}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          layers={['grid', 'axes', 'nodes', 'markers', 'mesh', 'legends', (layerProps) => {
            // Custom layer to render ISO labels below nodes
            const { nodes, xScale, yScale } = layerProps;
            
            if (!nodes || !Array.isArray(nodes)) return null;
            
            return (
              <g>
                {nodes.map((node, index) => {
                  if (!node || !node.data || !node.data.iso3 || node.data.iso3 === 'N/A') return null;
                  
                  // Get node position - node.x and node.y are already in pixel coordinates
                  const x = node.x;
                  const y = node.y;
                  const nodeSize = node.size || 10;
                  const labelY = y + (nodeSize / 2) + 15; // Position below the circle
                  
                  return (
                    <text
                      key={`label-${node.id || index}-${node.data.iso3}`}
                      x={x}
                      y={labelY}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="600"
                      fill={isDarkMode ? '#d1d5db' : '#374151'}
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {node.data.iso3}
                    </text>
                  );
                })}
              </g>
            );
          }]}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              translateX: 130,
              translateY: 0,
              itemWidth: 120,
              itemHeight: 20,
              itemsSpacing: 2,
              symbolSize: 12,
              symbolShape: 'circle',
              itemDirection: 'left-to-right',
              itemTextColor: isDarkMode ? '#ffffff' : '#374151',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Economic Complexity Index (ECI) →',
            legendOffset: 46,
            legendPosition: 'middle'
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '← Lower Political Risk (PRI)',
            legendOffset: -60,
            legendPosition: 'middle'
          }}
          markers={[
            {
              axis: 'x',
              value: 0,
              lineStyle: { stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '6,6' }
            },
            {
              axis: 'y',
              value: 3,
              lineStyle: { stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '6,6' }
            }
          ]}
          tooltip={({ node }) => {
            if (!node || !node.data) return null;
            const data = node.data;
            const opportunityColor = (data.untappedOpportunity || 0) >= 0 ? '#10b981' : '#ef4444';
            
            return (
              <div className={`${isDarkMode ? 'bg-gray-900 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} p-5 rounded-lg shadow-2xl border-2 min-w-[320px] max-w-[400px] transition-colors duration-200`} style={{ zIndex: 9999 }}>
                {/* Header */}
                <div className={`flex items-center justify-between mb-4 pb-3 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                   <div className="flex items-center">
                     <div 
                       className="w-4 h-4 rounded-full mr-3"
                       style={{ backgroundColor: node.color || getNodeColor(node) }}
                     ></div>
                    <div className={`font-bold text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {data.country || 'Unknown'}
                    </div>
                  </div>
                  <div className={`text-sm font-mono ${isDarkMode ? 'text-gray-400 bg-gray-800' : 'text-gray-600 bg-gray-100'} px-3 py-1 rounded`}>
                    {data.iso3 || 'N/A'}
                  </div>
                </div>
                
                {/* Geopolitical Group - Show for all countries */}
                {data.geopoliticalLabel && data.geopoliticalLabel !== 'Neutral' && (
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} p-3 rounded mb-4`}>
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-1`}>
                      Geopolitical Group
                    </div>
                    <div className={`text-lg font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {data.geopoliticalLabel}
                    </div>
                  </div>
                )}
                
                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} p-3 rounded`}>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wide mb-1`}>ECI</div>
                    <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{(data.x || 0).toFixed(2)}</div>
                  </div>
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} p-3 rounded`}>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wide mb-1`}>PRI</div>
                    <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{(data.y || 0).toFixed(1)}</div>
                  </div>
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} p-3 rounded`}>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wide mb-1`}>Reserves</div>
                    <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{(data.reservesPercentage || 0).toFixed(1)}%</div>
                  </div>
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} p-3 rounded`}>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wide mb-1`}>Imports</div>
                    <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{(data.importSharePercentage || 0).toFixed(1)}%</div>
                  </div>
                </div>
                
                {/* Opportunity Highlight */}
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} p-4 rounded`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wide`}>Untapped Opportunity</div>
                    <div className={`text-sm font-medium px-2 py-1 rounded ${isDarkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-200'}`}>
                      {data.opportunityLevel || 'Unknown'}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div 
                      className="text-2xl font-bold"
                      style={{ color: opportunityColor }}
                    >
                      {(data.untappedOpportunity || 0) >= 0 ? '+' : ''}{(data.untappedOpportunity || 0).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            );
          }}
          theme={getNivoTheme(isDarkMode)}
        />
        
        {/* Quadrant Labels - positioned absolutely within chart container */}
        <div className="absolute inset-0 pointer-events-none" style={{ 
          top: '40px', 
          left: '70px', 
          right: '120px', 
          bottom: '80px' 
        }}>
          {/* Top Left Quadrant - Strategic and Less Complex (ECI < 0, PRI > 3) */}
          <div 
            className={`absolute text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} font-semibold text-center`} 
            style={{ 
              top: '15%', 
              left: '25%', 
              transform: 'translate(-50%, -50%)',
              whiteSpace: 'nowrap'
            }}
          >
            Strategic and Less Complex
          </div>
          
          {/* Top Right Quadrant - Strategic and More Complex (ECI > 0, PRI > 3) */}
          <div 
            className={`absolute text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} font-semibold text-center`} 
            style={{ 
              top: '15%', 
              right: '25%', 
              transform: 'translate(50%, -50%)',
              whiteSpace: 'nowrap'
            }}
          >
            Strategic and More Complex
          </div>
          
          {/* Bottom Left Quadrant - Risk and Less Complex (ECI < 0, PRI < 3) */}
          <div 
            className={`absolute text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} font-semibold text-center`} 
            style={{ 
              bottom: '15%', 
              left: '25%', 
              transform: 'translate(-50%, 50%)',
              whiteSpace: 'nowrap'
            }}
          >
            Risk and Less Complex
          </div>
          
          {/* Bottom Right Quadrant - Risky and More Complex (ECI > 0, PRI < 3) */}
          <div 
            className={`absolute text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} font-semibold text-center`} 
            style={{ 
              bottom: '15%', 
              right: '25%', 
              transform: 'translate(50%, 50%)',
              whiteSpace: 'nowrap'
            }}
          >
            Risky and More Complex
          </div>
        </div>
      </div>

    </div>
  );
};

export default DiversificationOpportunityMatrix;
